import { Link } from "react-router-dom";
import { ArrowUpRight, ClipboardList } from "lucide-react";

/**
 * Card showing a service with image, copy, and TWO actions:
 *  - whole card links to /services/:slug (stretched link)
 *  - explicit "Quick quote" button → /contact?service=Name (z-10, above stretched link)
 *
 * Variants:
 *  - "tile" — used in homepage grid (image header + body)
 *  - "compact" — text-only, smaller (e.g. on detail "other services")
 */
export default function ServiceCard({ service, variant = "tile", testIdPrefix = "service-card" }) {
  const detailHref = `/services/${service.slug}`;
  const quoteHref = `/contact?service=${encodeURIComponent(service.name)}`;

  if (variant === "compact") {
    return (
      <article className="relative group bg-card hover:bg-secondary transition-colors p-5 flex flex-col gap-2 min-h-[140px]">
        <span className="overline">{service.accent}</span>
        <h3 className="mt-auto font-display text-base font-bold tracking-tight leading-tight text-foreground group-hover:text-accent transition-colors">
          <Link
            to={detailHref}
            data-testid={`${testIdPrefix}-${service.slug}`}
            className="before:absolute before:inset-0 before:content-['']"
          >
            {service.name}
          </Link>
        </h3>
      </article>
    );
  }

  return (
    <article className="relative group bg-card hover:bg-secondary border-r border-b border-border last:border-r-0 transition-colors overflow-hidden flex flex-col">
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        <img
          src={service.image}
          alt={service.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover duotone transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
          <span className="text-[11px] font-bold tracking-[0.28em] uppercase text-white">{service.accent}</span>
          <ArrowUpRight className="w-5 h-5 text-white/90 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>

      <div className="p-5 sm:p-6 flex flex-col gap-3 flex-1">
        <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-foreground leading-tight">
          <Link
            to={detailHref}
            data-testid={`${testIdPrefix}-${service.slug}`}
            className="before:absolute before:inset-0 before:content-[''] hover:text-accent transition-colors"
          >
            {service.name}
          </Link>
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed flex-1">{service.short}</p>
        <div className="pt-2 flex items-center justify-between">
          <Link
            to={quoteHref}
            data-testid={`quick-quote-${service.slug}`}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 inline-flex items-center gap-1.5 bg-accent text-white px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] hover:bg-accent/90 transition-colors"
          >
            <ClipboardList className="w-3.5 h-3.5" /> Quick quote
          </Link>
          <span className="text-xs font-medium text-accent group-hover:underline">Details →</span>
        </div>
      </div>
    </article>
  );
}
